/**
 * ChottuLink smart-link helpers.
 *
 * ChottuLink owns all of the app hand-off routing, so the web side never
 * builds a raw `projector://` URL or a store-fallback timer by hand:
 *   • app installed  → opens the app on the matching screen
 *   • not installed  → App Store / Play Store, then the SDK replays the params
 *                      on first launch (deferred deep linking)
 *   • desktop/no app → the link's configured web fallback
 *
 * Universal links on our own domain would skip the store for people with no
 * app, but they need an apple-app-site-association file served from the site
 * root — the hosting there isn't ours to change, so ChottuLink's domain (which
 * already hosts its own association file) does the job instead.
 *
 * The same domain and UA test are copy-pasted across /share, /contribute,
 * /contribute-mobile-upload, /mobile-upload and the gift flow; new code should
 * use these instead of adding a sixth copy.
 */
export const CHOTTULINK_DOMAIN = 'projectorapp.chottu.link';

// Desktop can't open the app at all, so every hand-off is gated on this.
export const isMobileDevice = () => /iphone|ipad|ipod|android/i.test(
  (typeof navigator !== 'undefined' && navigator.userAgent) || ''
);

/**
 * buildSmartLink('tvlogin', { code: '310378' })
 *   → https://projectorapp.chottu.link/tvlogin?code=310378
 */
export const buildSmartLink = (path, params) => {
  const query = new URLSearchParams(
    Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  return `https://${CHOTTULINK_DOMAIN}/${path}${query ? `?${query}` : ''}`;
};

// Custom URL scheme registered by the native app (projector://tvlogin?code=…).
//
// Why this exists alongside buildSmartLink: iOS Safari only opens a UNIVERSAL
// link (the https chottu link) when the user actually taps an anchor. A
// JavaScript navigation to one is ignored — Safari just loads the page, chottu
// can't hand off, and an installed app never opens. Custom schemes have no such
// restriction, so they're the only way to hand off AUTOMATICALLY. The trade-off
// is the opposite one: a scheme with no app behind it shows an error instead of
// falling back to the store, so the https smart link stays the target of every
// user-tappable button.
export const APP_SCHEME = 'projector';

export const buildAppSchemeLink = (path, params) => {
  const query = new URLSearchParams(
    Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  return `${APP_SCHEME}://${path}${query ? `?${query}` : ''}`;
};
